import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';

import { styled } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { GetOneResult, Identifier, ReduxState, useDataProvider, useTranslate } from 'react-admin';
import DashboardBox from '../../commons/dashboard-box/dashboard-box';
import GetMetabaseUrlOfTheData from '../../commons/get-metabase-url-of-the-data/get-metabase-url-of-the-data';
import { ResponseForwarderToMetabaseType } from '../../utils/ticketingBridgeMetabase/viewsDynamic';
import { Avatar, ChartDashboardComponent } from '../../utils/types';
import TableChartIcon from '@material-ui/icons/TableChart';
import extractIdFromURI from '../../utils/id';
import ShowTreeDialog from '../../commons/dialog/ShowTreeDialog';
import { useDispatch, useSelector } from 'react-redux';
import { CustomReducerState } from '../../redux/reducers';
import { AVATARS } from '../../providers/resources';
import { updateGlobalDashboard } from '../../redux/actions/dashboard';
import OptionMenu from '../statistics/actions/OptionMenu';
import { MenuItemType } from '../../commons/menuButtonActions';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import makeStyles from '@material-ui/styles/makeStyles';

// const QUESTION = 6;
const CustomTypography = styled(Typography)({
    fontSize: 72,
    fontWeight: 500,
});

const RenderData = ({ data }: { data: ResponseForwarderToMetabaseType }) => {
    const mData = data && 'data' in data ? data.data : null;
    const rows = mData && 'rows' in mData ? mData['rows'] : [];
    return (
        <div>
            <CustomTypography align={'center'}>
                {Array.isArray(rows) && rows.length > 0 ? rows[0][0] : '0'}
            </CustomTypography>
        </div>
    );
};

interface Props extends ChartDashboardComponent {
    infoText: string;
    dialogTitle: string;
    label: string;
    dashboardTitle: string;
    dialogHelpText: string;
    requestQuestionId: number;
    menuOptionLabel: {
        [key: string]: string;
    };
}
const useStyles = makeStyles(() => ({
    boldUppercase: {
        padding: 0,
        textTransform: 'uppercase',
        fontSize: 13,
        fontWeight: 'bold',
    },
}));

interface ConfigProp {
    [key: string]: Identifier;
}
const PLACE = 'place';

const TotalNumberOfIncidentsByCompanyDashboard = (props: Props) => {
    const classes = useStyles();
    const translate = useTranslate();

    const dashboardVersion: number = useSelector((state: CustomReducerState) => state.ticketing.dashboards.version);
    const [version, setVersion] = useState(0);

    const dispatch = useDispatch();
    const dataProvider = useDataProvider();

    const config: ConfigProp | undefined = useSelector((state: CustomReducerState) => {
        const ownDashboardType = state.ticketing.dashboards.global[props.id];
        const chartRegisterData = ownDashboardType.find((item) => item.instanceId === props.instanceId);
        return chartRegisterData ? chartRegisterData.customFields : undefined;
    });

    const placeIdSelected: string | undefined = config && PLACE in config ? (config[PLACE] as string) : undefined;
    const [placeSelected, setPlaceSelected] = useState<Avatar | undefined>(undefined);
    const [showTreeDialog, setShowTreeDialog] = useState<boolean>(false);

    const [eventRunningFilter, setEventRunningFilter] = useState<boolean>(true);

    // update version when global dashboard version change
    useEffect(() => {
        if (dashboardVersion) {
            setVersion(version + 1);
        }
    }, [dashboardVersion]);

    // get place selected and update version
    useEffect(() => {
        if (placeSelected) {
            setVersion(version + 1);
        }
    }, [placeSelected]);

    useEffect(() => {
        if (placeIdSelected) {
            // search place
            dataProvider
                .getOne(
                    AVATARS,
                    { id: placeIdSelected },
                    {
                        onSuccess: (response: GetOneResult) => {
                            if (response && 'data' in response && response.data) {
                                // @ts-ignore
                                setPlaceSelected(response.data as Avatar);
                            }
                        },
                        onFailure: (error: any) => {
                            console.error('Failure getting one ', AVATARS, error);
                            setPlaceSelected(undefined);
                        },
                    }
                )
                .catch((e: any) => {
                    console.error('Catch exception getting one ', e);
                    setPlaceSelected(undefined);
                });
        }
    }, [placeIdSelected]);

    const validateSelectedPlace = (place?: Avatar) => {
        if (!place) return;
        updateConfig({ [PLACE]: place.id });
    };

    const handleChangeEventRunning = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEventRunningFilter(event.target.checked);
        setVersion(version + 1);
    };

    const updateConfig = (config: ConfigProp) => {
        dispatch(updateGlobalDashboard.fn(props.id, props.instanceId, config));
    };

    const optionMenu: MenuItemType[] = [
        {
            icon: null,
            text: translate('app.dashboards.totalNumberOfIncidentsByCompanyDashboard.eventRunning'),
            disabled: false,
            renderComponent: () => (
                <FormControlLabel
                    control={
                        <Checkbox
                            color={'primary'}
                            checked={eventRunningFilter}
                            onChange={handleChangeEventRunning}
                            name='eventRunning'
                        />
                    }
                    label={
                        <Typography color={'primary'} variant={'body1'} className={classes.boldUppercase}>
                            {translate('app.dashboards.totalNumberOfIncidentsByCompanyDashboard.eventRunning')}
                        </Typography>
                    }
                />
            ),
            roles: [],
        },
        {
            icon: null,
            onClick: () => setShowTreeDialog(true),
            text: translate('app.dashboards.quantityOfOpenAndContractedIncidentsDashboard.place'),
            disabled: false,
            roles: [],
        },
    ];

    return (
        <>
            <DashboardBox
                onClose={props.close}
                config={<OptionMenu buttonLabel={'app.dashboards.option'} menuActions={optionMenu} />}
                icon={
                    <Tooltip title={translate(props.infoText, { question: props.requestQuestionId })}>
                        <TableChartIcon />
                    </Tooltip>
                }
                subtitle={
                    placeSelected
                        ? translate('app.dashboards.totalNumberOfIncidentsByCompanyDashboard.in', {
                              name: (placeSelected as Avatar).name,
                          })
                        : translate('app.dashboards.totalNumberOfIncidentsByCompanyDashboard.nonePlace')
                }
                title={translate('app.dashboards.totalNumberOfIncidentsByCompanyDashboard.title')}
            >
                {placeSelected && (
                    <GetMetabaseUrlOfTheData
                        version={version}
                        requestBody={{
                            id: props.requestQuestionId,
                            dashboard_type: 'question',
                            place_id: placeSelected ? extractIdFromURI(placeSelected.id as string) : null,
                            event_running: eventRunningFilter ? 'true' : 'false',
                        }}
                    >
                        {(data: any) => <RenderData data={data} />}
                    </GetMetabaseUrlOfTheData>
                )}
            </DashboardBox>
            {showTreeDialog && (
                <ShowTreeDialog
                    closeAfterValidate={true}
                    dialogTitle={props.dialogTitle}
                    dialogHelpText={props.dialogHelpText}
                    handleValidate={validateSelectedPlace}
                    showButton={false}
                    onClose={() => setShowTreeDialog(false)}
                />
            )}
        </>
    );
};

export default TotalNumberOfIncidentsByCompanyDashboard;
